/* Environment variables page: pure model. The scope read from the URL, the list filters and the rows shown. */
import type { Environment, Platform } from '@hangar/shared';
import type { EnvVarFilters } from '@/api/endpoints';
import type { EnvVar } from '@/api/schemas';
import { needsApp, needsEnvironment, needsPlatform, type ScopeKey } from './envHelpers';
import type { ScopeSelection } from './ScopePicker';

export const SCOPES: ScopeKey[] = ['org', 'project', 'project-env', 'app', 'app-env', 'platform'];

/** The query string of the page (`EnvSearch` in the router). */
export interface EnvPageSearch {
  scope?: string;
  app?: string;
  environment?: string;
  platform?: string;
  tab?: string;
}

interface ProjectLike {
  id: string;
  environments: Environment[];
}

const parseScopeKey = (raw: string | undefined): ScopeKey => (SCOPES.includes(raw as ScopeKey) ? (raw as ScopeKey) : 'app-env');

/** The staging environment, else the first one. */
const defaultEnvironment = (project: ProjectLike | undefined): string | undefined => project?.environments.find((e) => e.kind === 'staging')?.slug ?? project?.environments[0]?.slug;

/** The scope the page shows: the URL wins, then the first app and the staging environment. */
export function scopeFromSearch(search: EnvPageSearch, apps: Array<{ slug: string }> | undefined, project: ProjectLike | undefined): ScopeSelection {
  return {
    scope: parseScopeKey(search.scope),
    appSlug: search.app ?? apps?.[0]?.slug,
    environment: search.environment ?? defaultEnvironment(project),
    platform: search.platform as Platform | undefined,
    includeInherited: false,
  };
}

/** The list can be fetched once every narrowing the scope needs is picked (and the project exists). */
export function isScopeReady(scope: ScopeSelection, hasProject: boolean, hasApp: boolean): boolean {
  if (!hasProject) return false;
  if (needsApp(scope.scope) && !hasApp) return false;
  if (needsEnvironment(scope.scope) && !scope.environment) return false;
  return !needsPlatform(scope.scope) || Boolean(scope.platform);
}

/** Variables not bound to an environment are asked for with the `none` environment, except at organization scope. */
function environmentFilter(scope: ScopeSelection): string | undefined {
  if (needsEnvironment(scope.scope)) return scope.environment;
  return scope.scope === 'org' ? undefined : 'none';
}

export function envVarFilters(scope: ScopeSelection, projectId: string | undefined, appId: string | undefined, inherited: boolean): EnvVarFilters {
  const f: EnvVarFilters = {};
  if (scope.scope !== 'org') f.projectId = projectId;
  if (needsApp(scope.scope)) f.appId = appId;
  f.environment = environmentFilter(scope);
  if (needsPlatform(scope.scope)) f.platform = scope.platform;
  if (needsApp(scope.scope) && inherited) f.includeInherited = true;
  return f;
}

const expectedScope = (scope: ScopeKey): 'org' | 'app' | 'project' => {
  if (scope === 'org') return 'org';
  return needsApp(scope) ? 'app' : 'project';
};

/** Belt and braces over the API filter: only the requested scope (plus inherited rows when asked). */
export function visibleRows(data: EnvVar[] | undefined, scope: ScopeSelection): EnvVar[] | undefined {
  if (!data) return undefined;
  const expect = expectedScope(scope.scope);
  return data.filter((v) => v.inherited || (v.scope === expect && (!needsPlatform(scope.scope) || v.platform === scope.platform)));
}

/** Live environments, in the project's own order. */
export const activeEnvironments = (project: ProjectLike): Environment[] => [...project.environments].filter((e) => !e.archivedAt).sort((a, b) => a.sortOrder - b.sortOrder);

/** The URL after picking a scope; the tab is kept. */
export function scopeSearch(next: ScopeSelection, tab: string | undefined): EnvPageSearch {
  return { scope: next.scope, app: next.appSlug, environment: next.environment, platform: next.platform, tab };
}

/** The URL after switching tab: the default tab is not written. */
export function tabSearch(search: EnvPageSearch, tab: string): EnvPageSearch {
  return { ...search, tab: tab === 'variables' ? undefined : tab };
}
